class ProductSet extends HTMLElement {
  constructor() {
    super();
    this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
    this.setTitle = this.dataset.setTitle || 'Set';
    this.setHandle = this.dataset.setHandle || this.getSetHandleFromUrl();
    this.setProductId = this.dataset.setProductId || '';
    this.cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
    this.submitButton = this.querySelector('[data-set-submit]');
    this.submitButtonText = this.querySelector('[data-set-submit-text]');
    this.message = this.querySelector('[data-set-message]');
    this.priceNode = this.querySelector('[data-set-total]');
    this.builder = this.querySelector('[data-set-builder]');
    this.previews = {
      top: Array.from(this.querySelectorAll('[data-set-preview="top"]')),
      bottom: Array.from(this.querySelectorAll('[data-set-preview="bottom"]'))
    };
    this.bottomTabs = Array.from(this.querySelectorAll('[data-bottom-tab]'));
    this.bottomCards = Array.from(this.querySelectorAll('[data-bottom-card]'));
    this.products = {};
    this.productMedia = {};
    this.canonicalProducts = {};
    this.canonicalProductsLoaded = false;
    this.isReadyToAdd = false;

    this.querySelectorAll('[data-set-product-json]').forEach((script) => {
      this.products[script.dataset.productKey] = JSON.parse(script.textContent);
    });

    this.querySelectorAll('[data-set-product-media-json]').forEach((script) => {
      this.productMedia[script.dataset.productKey] = JSON.parse(script.textContent);
    });
  }

  getSetHandleFromUrl() {
    return window.location.pathname
      .replace(/^\/[a-z]{2}(?:-[a-z]{2})?\//i, '/')
      .split('/products/')[1]
      ?.split('/')[0]
      ?.split('?')[0] || '';
  }

  buildSetGroupId() {
    const randomPart = Math.random().toString(36).slice(2, 8);
    return `set-${this.setHandle || 'product'}-${Date.now()}-${randomPart}`;
  }

  connectedCallback() {
    this.addEventListener('change', this.onOptionChange.bind(this));
    this.bottomTabs.forEach((tab) => tab.addEventListener('click', this.onBottomTabClick.bind(this)));
    this.submitButton?.addEventListener('click', this.onSubmit.bind(this));
    this.hideAvailabilityWidget();
    document.documentElement.classList.add('product-set-page-active');
    if (!this.getDefaultLocalePath()) {
      this.syncMatchingColorOptions();
    }
    this.refresh();
    this.loadCanonicalProducts().then(() => {
      this.canonicalProductsLoaded = true;
      this.syncMatchingColorOptions();
      this.refresh();
    });
  }

  onBottomTabClick(event) {
    const selectedIndex = event.currentTarget.dataset.bottomTab;

    this.bottomTabs.forEach((tab) => {
      const isSelected = tab.dataset.bottomTab === selectedIndex;
      tab.setAttribute('aria-selected', isSelected ? 'true' : 'false');
    });

    this.bottomCards.forEach((card) => {
      card.hidden = card.dataset.bottomCard !== selectedIndex;
    });

    this.markReadyToAdd();
    this.syncMatchingColorOptions();
    this.refresh();
  }

  onOptionChange(event) {
    const input = event.target.closest('[data-set-option-input]');
    if (!input) return;

    const option = input.closest('[data-set-option]');
    const value = option?.querySelector('[data-set-selected-value]');
    if (value) value.textContent = input.value;

    this.markReadyToAdd();
    this.syncMatchingColorOptions();
    this.refresh();
  }

  isColorOptionName(name) {
    const normalizedName = String(name || '').trim().toLowerCase();
    return ['color', 'colour', 'اللون'].includes(normalizedName);
  }

  normalizeOptionValue(value) {
    return String(value || '').trim().toLowerCase().replace(/\s+/g, ' ');
  }

  normalizeColorText(value) {
    return String(value || '')
      .trim()
      .toLowerCase()
      .replace(/[أإآ]/g, 'ا')
      .replace(/[ى]/g, 'ي')
      .replace(/[ة]/g, 'ه')
      .replace(/[\-_\/]+/g, ' ')
      .replace(/[^\p{L}\p{N}\s]+/gu, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  normalizeColorValue(value) {
    return this.normalizeColorText(value);
  }

  textContainsColorAlias(normalizedText, normalizedAlias) {
    if (!normalizedText || !normalizedAlias) return false;

    const escapedAlias = normalizedAlias.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const aliasPattern = new RegExp(`(^|\\s)${escapedAlias}(?=\\s|$)`, 'u');

    return aliasPattern.test(normalizedText);
  }

  colorTextMatchesValue(text, colorValue) {
    const normalizedText = this.normalizeColorText(text);
    const normalizedColor = this.normalizeColorValue(colorValue);
    if (!normalizedText || !normalizedColor) return false;

    return this.textContainsColorAlias(normalizedText, normalizedColor);
  }

  getColorOptionIndex(product) {
    if (!product?.options?.length) return -1;
    return product.options.findIndex((optionName) => this.isColorOptionName(optionName));
  }

  getDefaultLocalePath() {
    const localizedPath = window.location.pathname.replace(/^\/[a-z]{2}(?:-[a-z]{2})?\//i, '/');
    if (localizedPath === window.location.pathname) return null;

    return `${localizedPath}${window.location.search || ''}`;
  }

  async loadCanonicalProductsFromDefaultPage() {
    const defaultLocalePath = this.getDefaultLocalePath();
    if (!defaultLocalePath) return;

    try {
      const response = await fetch(defaultLocalePath, {
        credentials: 'same-origin',
        headers: { Accept: 'text/html' }
      });

      if (!response.ok) return;

      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const scripts = Array.from(doc.querySelectorAll('[data-set-product-json]'));

      scripts.forEach((script) => {
        try {
          const canonicalProduct = JSON.parse(script.textContent);
          if (!canonicalProduct?.variants?.length) return;

          const matchingKey = Object.keys(this.products).find((productKey) => {
            return String(this.products[productKey]?.id) === String(canonicalProduct.id);
          }) || script.dataset.productKey;

          if (matchingKey && this.products[matchingKey]) {
            this.canonicalProducts[matchingKey] = canonicalProduct;
          }
        } catch (error) {}
      });
    } catch (error) {}
  }

  async loadCanonicalProducts() {
    await this.loadCanonicalProductsFromDefaultPage();

    const entries = Object.entries(this.products);
    const isLocalizedPage = Boolean(this.getDefaultLocalePath());

    await Promise.all(entries.map(async ([productKey, product]) => {
      if (!product?.handle || this.canonicalProducts[productKey]) return;

      try {
        const response = await fetch(`/products/${encodeURIComponent(product.handle)}.js`, {
          credentials: 'same-origin',
          headers: { Accept: 'application/json' }
        });

        if (!response.ok) throw new Error(`Could not load canonical product ${product.handle}`);

        const canonicalProduct = await response.json();
        this.canonicalProducts[productKey] = canonicalProduct?.variants?.length
          ? canonicalProduct
          : product;
      } catch (error) {
        if (!isLocalizedPage) {
          this.canonicalProducts[productKey] = product;
        }
      }
    }));
  }

  getCanonicalVariant(productKey, variantId) {
    const canonicalProduct = this.canonicalProducts[productKey];
    if (!canonicalProduct?.variants?.length || variantId == null) return null;

    return canonicalProduct.variants.find((variant) => String(variant.id) === String(variantId)) || null;
  }

  getVariantCanonicalOption(productKey, variant, optionIndex) {
    const canonicalVariant = this.getCanonicalVariant(productKey, variant?.id);
    return canonicalVariant?.options?.[optionIndex] || variant?.options?.[optionIndex] || null;
  }

  getCanonicalColorForVariant(productKey, product, variant) {
    const colorIndex = this.getColorOptionIndex(product);
    if (colorIndex === -1 || !variant) return null;

    return this.getVariantCanonicalOption(productKey, variant, colorIndex);
  }

  getInputCanonicalColor(card, product, input) {
    const colorIndex = this.getColorOptionIndex(product);
    if (colorIndex === -1 || !input) return input?.value || null;

    const siblingInputs = Array.from(input.closest('[data-set-option]')?.querySelectorAll('[data-set-option-input]') || []);
    const hasDuplicateTranslatedValue = siblingInputs.filter((sibling) => sibling.value === input.value).length > 1;
    const inputVariant = product?.variants?.find((variant) => {
      return String(variant.id) === String(input.dataset.optionVariantId || '');
    });
    const inputCanonicalColor = this.getCanonicalColorForVariant(card.dataset.productKey, product, inputVariant);
    if (inputCanonicalColor) return inputCanonicalColor;

    const matchingVariant = product?.variants?.find((variant) => {
      return variant.options?.[colorIndex] === input.value;
    });

    if (!hasDuplicateTranslatedValue) {
      const canonicalColor = this.getCanonicalColorForVariant(card.dataset.productKey, product, inputVariant || matchingVariant);
      if (canonicalColor) return canonicalColor;
    }

    const canonicalColors = this.getProductColorValues(product, card.dataset.productKey);
    const valueIndex = Number(input.dataset.optionValueIndex);
    if (Number.isInteger(valueIndex) && canonicalColors[valueIndex]) {
      return canonicalColors[valueIndex];
    }

    return this.getCanonicalColorForVariant(card.dataset.productKey, product, matchingVariant) || input.value;
  }

  getProductColorValues(product, productKey) {
    const colorIndex = this.getColorOptionIndex(product);
    if (colorIndex === -1 || !product?.variants?.length) return [];

    const values = new Map();
    product.variants.forEach((variant) => {
      const value = this.getCanonicalColorForVariant(productKey, product, variant) || variant.options?.[colorIndex];
      const normalized = this.normalizeColorValue(value);
      if (normalized && !values.has(normalized)) {
        values.set(normalized, value);
      }
    });

    return Array.from(values.values());
  }

  getSharedColorValues(primaryProduct, secondaryProduct, primaryProductKey, secondaryProductKey) {
    const primaryColors = this.getProductColorValues(primaryProduct, primaryProductKey);
    const secondaryColors = new Set(
      this.getProductColorValues(secondaryProduct, secondaryProductKey).map((value) => this.normalizeColorValue(value))
    );

    return primaryColors.filter((value) => secondaryColors.has(this.normalizeColorValue(value)));
  }

  getSelectedColorValue(card, product) {
    const colorIndex = this.getColorOptionIndex(product);
    if (colorIndex === -1) return null;

    const selectedVariant = this.getSelectedVariant(card);
    const selectedOptions = this.getSelectedOptions(card);

    return this.getCanonicalColorForVariant(card.dataset.productKey, product, selectedVariant) || selectedOptions[colorIndex] || null;
  }

  getColorOptionFieldset(card, product) {
    const colorIndex = this.getColorOptionIndex(product);
    if (colorIndex === -1) return null;

    return card.querySelector(`[data-set-option][data-option-position="${colorIndex + 1}"]`);
  }

  getActiveBottomCard() {
    return this.querySelector('[data-bottom-card]:not([hidden])');
  }

  applyColorAvailability(card, product, allowedColors, preferredColors = []) {
    const fieldset = this.getColorOptionFieldset(card, product);
    if (!fieldset) return null;
    const inputs = Array.from(fieldset.querySelectorAll('[data-set-option-input]'));

    if (!allowedColors.length) {
      return fieldset.querySelector('[data-set-option-input]:checked') || null;
    }

    const allowedColorSet = new Set(allowedColors.map((value) => this.normalizeColorValue(value)));
    const preferredColorList = preferredColors
      .map((value) => this.normalizeColorValue(value))
      .filter(Boolean);
    const visibleInputsByColor = new Map();

    let firstVisibleInput = null;
    let checkedVisibleInput = null;

    inputs.forEach((input) => {
      const label = fieldset.querySelector(`label[for="${input.id}"]`);
      const canonicalColor = this.getInputCanonicalColor(card, product, input);
      const normalizedValue = this.normalizeColorValue(canonicalColor);
      const isVisible = allowedColorSet.has(normalizedValue);

      input.disabled = !isVisible;
      input.hidden = !isVisible;
      if (label) label.hidden = !isVisible;
      if (!isVisible && input.checked) input.checked = false;

      if (isVisible && !firstVisibleInput) firstVisibleInput = input;
      if (isVisible && input.checked) checkedVisibleInput = input;
      if (isVisible && !visibleInputsByColor.has(normalizedValue)) {
        visibleInputsByColor.set(normalizedValue, input);
      }
    });

    if (!firstVisibleInput) {
      return null;
    }

    const preferredInput =
      preferredColorList.map((value) => visibleInputsByColor.get(value)).find(Boolean) || null;

    const nextInput = preferredInput || checkedVisibleInput || firstVisibleInput || null;

    if (nextInput && !nextInput.checked) {
      nextInput.checked = true;
    }

    const selectedValue = fieldset.querySelector('[data-set-selected-value]');
    if (selectedValue) {
      selectedValue.textContent = nextInput ? this.getInputCanonicalColor(card, product, nextInput) || nextInput.value : '';
    }

    return nextInput;
  }

  syncMatchingColorOptions() {
    const topCard = this.querySelector('[data-set-card="top"]');
    const bottomCard = this.getActiveBottomCard();
    if (!topCard || !bottomCard) return;

    const topProduct = this.products[topCard.dataset.productKey];
    const bottomProduct = this.products[bottomCard.dataset.productKey];
    if (!topProduct || !bottomProduct) return;
    if (
      this.getDefaultLocalePath()
      && (
        !this.canonicalProductsLoaded
        || !this.canonicalProducts[topCard.dataset.productKey]
        || !this.canonicalProducts[bottomCard.dataset.productKey]
      )
    ) {
      return;
    }

    const sharedColors = this.getSharedColorValues(
      topProduct,
      bottomProduct,
      topCard.dataset.productKey,
      bottomCard.dataset.productKey
    );

    const currentTopColor = this.getSelectedColorValue(topCard, topProduct);
    const currentBottomColor = this.getSelectedColorValue(bottomCard, bottomProduct);

    const topInput = this.applyColorAvailability(topCard, topProduct, sharedColors, [
      currentTopColor,
      currentBottomColor
    ]);
    const resolvedTopColor = topInput ? this.getInputCanonicalColor(topCard, topProduct, topInput) : currentTopColor;

    this.applyColorAvailability(bottomCard, bottomProduct, sharedColors, [
      resolvedTopColor,
      currentBottomColor
    ]);
  }

  getActiveComponents() {
    const components = [];
    const topCard = this.querySelector('[data-set-card="top"]');
    const bottomCard = this.querySelector('[data-bottom-card]:not([hidden])');

    if (topCard) components.push(topCard);
    if (bottomCard) components.push(bottomCard);

    return components;
  }

  getSelectedOptions(card) {
    return Array.from(card.querySelectorAll('[data-set-option]')).map((option) => {
      const checked = option.querySelector('[data-set-option-input]:checked');
      return checked ? checked.value : null;
    });
  }

  getSelectedVariant(card) {
    const product = this.products[card.dataset.productKey];
    if (!product) return null;

    if (!product.variants || product.variants.length === 0) return null;
    if (product.variants.length === 1) return product.variants[0];

    const selectedOptions = this.getSelectedOptions(card);
    return product.variants.find((variant) => {
      return selectedOptions.every((value, index) => variant.options[index] === value);
    });
  }

  refresh() {
    const activeCards = this.getActiveComponents();
    let total = 0;
    let canSubmit = activeCards.length >= 2;

    activeCards.forEach((card) => {
      const variant = this.getSelectedVariant(card);
      const price = card.querySelector('[data-component-price]');
      const status = card.querySelector('[data-component-status]');

      if (!variant || !variant.available) canSubmit = false;
      if (variant) total += variant.price;
      if (price && variant) price.textContent = this.formatMoney(variant.price);
      if (status) status.textContent = variant && !variant.available ? 'Sold out' : '';
      if (variant) this.updatePreview(card, variant);
    });

    if (this.priceNode) this.priceNode.textContent = this.formatMoney(total);
    this.updateInstallmentWidgets(total);
    if (this.submitButton) this.submitButton.disabled = !canSubmit;
  }

  updateInstallmentWidgets(totalCents) {
    const total = Number(totalCents || 0) / 100;
    const tabbyInstallment = total / 4;

    document.querySelectorAll('.installment-price').forEach((node) => {
      node.textContent = tabbyInstallment.toFixed(2);
    });

    this.querySelectorAll('tamara-widget[amount]').forEach((widget) => {
      widget.setAttribute('amount', total.toFixed(2));
    });
  }

  async onSubmit(event) {
    event.preventDefault();

    if (!this.isReadyToAdd) {
      this.showMessage('Please select your top and trouser options first.');
      return;
    }

    if (this.getDefaultLocalePath() && !this.canonicalProductsLoaded) {
      this.showMessage('Set colors are still loading. Please try again.');
      return;
    }

    this.syncMatchingColorOptions();

    const cards = this.getActiveComponents();
    const selectedColors = cards.map((card) => {
      const product = this.products[card.dataset.productKey];
      return this.getSelectedColorValue(card, product);
    });
    const normalizedColors = selectedColors.map((color) => this.normalizeColorValue(color)).filter(Boolean);

    if (normalizedColors.length !== cards.length || new Set(normalizedColors).size !== 1) {
      this.showMessage('Please select the same color for the top and trouser.');
      return;
    }

    const variants = cards.map((card) => ({ card, variant: this.getSelectedVariant(card) }));
    const unavailable = variants.find((item) => !item.variant || !item.variant.available);

    if (unavailable) {
      this.showMessage('Please select available sizes for the full set.');
      return;
    }

    this.setLoading(true);
    this.showMessage('');

    const setGroupId = this.buildSetGroupId();
    const items = variants.map((item, index) => {
      const componentLabel = item.card.dataset.componentLabel || 'Set item';

      return {
        id: item.variant.id,
        quantity: 1,
        properties: {
          'Set Name': this.setTitle,
          'Set Component': componentLabel,
          '_set': 'true',
          '_set_title': this.setTitle,
          '_set_handle': this.setHandle,
          '_set_product_id': this.setProductId,
          '_set_group_id': setGroupId,
          '_set_component': componentLabel,
          '_set_component_product_key': item.card.dataset.productKey || '',
          '_set_component_variant_id': String(item.variant.id),
          '_set_component_variant_sku': item.variant.sku || '',
          '_set_component_color': selectedColors[index] || ''
        }
      };
    });

    const payload = { items };
    if (this.cart) {
      payload.sections = this.cart.getSectionsToRender().map((section) => section.id);
      payload.sections_url = window.location.pathname;
      this.cart.setActiveElement(document.activeElement);
    }

    try {
      const config = typeof fetchConfig === 'function' ? fetchConfig('javascript') : {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' }
      };
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      config.body = JSON.stringify(payload);

      const response = await fetch(window.routes.cart_add_url, config);
      const responseText = await response.text();
      const contentType = response.headers.get('content-type') || '';

      if (!contentType.includes('json') && !contentType.includes('javascript')) {
        throw new Error('Shopify returned a page instead of cart data. Please refresh and try again.');
      }

      const parsedState = JSON.parse(responseText);

      if (!response.ok || parsedState.status) {
        throw new Error(parsedState.description || parsedState.message || 'Could not add this set.');
      }

      if (this.cart) {
        await this.renderFreshCartDrawer();
      } else {
        window.location = window.routes.cart_url;
      }
    } catch (error) {
      this.showMessage(error.message || 'Could not add this set. Please try again.');
    } finally {
      this.setLoading(false);
    }
  }

  setLoading(isLoading) {
    if (!this.submitButton) return;
    this.submitButton.setAttribute('aria-disabled', isLoading ? 'true' : 'false');
    this.submitButton.classList.toggle('loading', isLoading);
  }

  markReadyToAdd() {
    this.isReadyToAdd = true;
    this.classList.add('is-ready-to-add');
    if (this.submitButtonText) this.submitButtonText.textContent = this.submitButton.dataset.addLabel || 'Add to bag';
    this.showMessage('');
  }

  showMessage(message) {
    if (!this.message) return;
    this.message.textContent = message;
  }

  formatMoney(cents) {
    const amount = (Number(cents || 0) / 100).toFixed(2);
    const amountNoDecimals = String(Math.round(Number(cents || 0) / 100));

    return this.moneyFormat
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, amountNoDecimals)
      .replace(/\{\{\s*amount\s*\}\}/, amount);
  }

  updatePreview(card, variant) {
    const previewKey = card.dataset.setCard === 'top' ? 'top' : 'bottom';
    const previews = this.previews[previewKey] || [];
    if (previews.length === 0 || !variant) return;

    const product = this.products[card.dataset.productKey];
    const selectedColor = this.getSelectedColorValue(card, product);
    const previewImages = this.getPreviewImages(card.dataset.productKey, variant, product, selectedColor, previews.length);
    const cardImage = card.querySelector('[data-component-image]');

    previews.forEach((preview, index) => {
      const image = previewImages[index] || previewImages[0];
      const img = preview.querySelector('img');
      const title = preview.querySelector('[data-set-preview-title]');
      const option = preview.querySelector('[data-set-preview-option]');

      if (img && image?.src) {
        img.src = image.src;
        img.srcset = '';
        img.alt = image.alt || product?.title || this.setTitle;
      }

      if (title && product?.title) title.textContent = product.title;
      if (option) {
        option.textContent = variant.options.filter(Boolean).join(' / ');
      }
    });

    if (cardImage && previewImages[0]?.src) {
      cardImage.src = previewImages[0].src;
      cardImage.alt = previewImages[0].alt || product?.title || this.setTitle;
    }
  }

  getVariantImage(variant, product) {
    if (variant.featured_image?.src) return variant.featured_image;
    if (variant.featured_media?.preview_image?.src) return variant.featured_media.preview_image;
    if (product?.featured_image) {
      if (typeof product.featured_image === 'string') return { src: product.featured_image, alt: product.title };
      if (product.featured_image.src) return product.featured_image;
    }
    if (Array.isArray(product?.images) && product.images.length > 0) {
      const firstImage = product.images[0];
      return typeof firstImage === 'string' ? { src: firstImage, alt: product.title } : firstImage;
    }
    return null;
  }

  getImageSrc(image) {
    if (!image) return '';
    if (typeof image === 'string') return image;
    return image.src || image.url || image.preview_image?.src || '';
  }

  getImageSrcKey(image) {
    return this.getImageSrc(image).split('?')[0].replace(/^https?:/, '');
  }

  mediaMatchesOtherColor(item, product, productKey, selectedColor) {
    const selectedColorKey = this.normalizeColorValue(selectedColor);
    const mediaText = `${item?.alt || ''} ${item?.src || ''}`;

    return this.getProductColorValues(product, productKey).some((color) => {
      const colorKey = this.normalizeColorValue(color);
      return colorKey && colorKey !== selectedColorKey && this.colorTextMatchesValue(mediaText, color);
    });
  }

  getGroupedMediaFromVariant(media, variantImage, product, productKey, selectedColor, limit) {
    const variantImageKey = this.getImageSrcKey(variantImage);
    if (!variantImageKey) return [];

    const startIndex = media.findIndex((item) => this.getImageSrcKey(item) === variantImageKey);
    if (startIndex === -1) return [];

    const groupedMedia = [];
    for (let index = startIndex; index < media.length && groupedMedia.length < limit; index += 1) {
      const item = media[index];
      if (index !== startIndex && this.mediaMatchesOtherColor(item, product, productKey, selectedColor)) break;
      groupedMedia.push(item);
    }

    return groupedMedia;
  }

  getPreviewImages(productKey, variant, product, selectedColor, limit) {
    const media = Array.isArray(this.productMedia[productKey]) ? this.productMedia[productKey] : [];
    const matchedMedia = media.filter((item) => {
      const mediaText = `${item?.alt || ''} ${item?.src || ''}`;
      return this.colorTextMatchesValue(mediaText, selectedColor);
    });
    const variantImage = this.getVariantImage(variant, product);
    const groupedMedia = this.getGroupedMediaFromVariant(media, variantImage, product, productKey, selectedColor, limit);

    if (matchedMedia.length > 0 || groupedMedia.length > 0) {
      return this.uniqueImages([variantImage, ...matchedMedia, ...groupedMedia]).slice(0, limit);
    }

    const fallbackImages = [
      variantImage,
      this.getAlternateVariantImage(variant, product),
      ...media
    ];

    return this.uniqueImages([...matchedMedia, ...fallbackImages]).slice(0, limit);
  }

  uniqueImages(images) {
    const seen = new Set();

    return images.filter((image) => {
      const imageKey = this.getImageSrcKey(image);
      if (!imageKey || seen.has(imageKey)) return false;
      seen.add(imageKey);
      return true;
    });
  }

  async renderFreshCartDrawer() {
    const requests = this.cart.getSectionsToRender().map(async (section) => {
      const sectionId = section.id === 'CartDrawer' ? section.section : section.id;
      const response = await fetch(`${window.routes.cart_url}?section_id=${sectionId}`);
      const html = await response.text();
      return { section, html };
    });

    const renderedSections = await Promise.all(requests);

    renderedSections.forEach(({ section, html }) => {
      const target = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      if (!target || !html) return;

      const selector = section.selector || '.shopify-section';
      const source = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
      if (!source) return;

      target.innerHTML = source.innerHTML;
    });

    this.cart.classList.remove('is-empty');
    this.cart.querySelector('.drawer__inner')?.classList.remove('is-empty');
    this.cart.open();
  }

  hideAvailabilityWidget() {
    const hideMatchingFixedNodes = () => {
      document.querySelectorAll('body *').forEach((node) => {
        const text = this.normalizeColorText(node.textContent || '');
        const hiddenPhrases = [
          'notify me when available',
          'اخبرني عند توفر المنتج',
          'اخبرني عند التوفر',
          'اخبرني عندما يتوفر'
        ].map((phrase) => this.normalizeColorText(phrase));

        if (!hiddenPhrases.some((phrase) => phrase && text.includes(phrase))) return;

        let fixedParent = null;
        let current = node;

        while (current && current !== document.body) {
          const position = window.getComputedStyle(current).position;
          if (position === 'fixed' || position === 'sticky') {
            fixedParent = current;
            break;
          }
          current = current.parentElement;
        }

        const target = fixedParent || node.closest('div, button, a, form');
        if (target) target.style.setProperty('display', 'none', 'important');
      });
    };

    hideMatchingFixedNodes();
    window.setTimeout(hideMatchingFixedNodes, 750);
    window.setTimeout(hideMatchingFixedNodes, 2000);

    const observer = new MutationObserver(hideMatchingFixedNodes);
    observer.observe(document.body, { childList: true, subtree: true });
  }

  getAlternateVariantImage(selectedVariant, product) {
    if (!product?.variants) return null;

    const selectedImage = this.getVariantImage(selectedVariant, product);
    const selectedSrc = selectedImage?.src;

    for (const variant of product.variants) {
      if (variant.id === selectedVariant.id) continue;

      const image = this.getVariantImage(variant, product);
      if (image?.src && image.src !== selectedSrc) return image;
    }

    return null;
  }
}

if (!customElements.get('product-set')) {
  customElements.define('product-set', ProductSet);
}
